import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const LangContext = createContext(null);

const STORAGE_KEY = "scudieri_lang";

export const translations = {
  it: {
    nav: {
      home: "Home",
      formats: "Format",
      services: "Servizi",
      events: "Eventi",
      gallery: "Galleria",
      contact: "Contatti",
      careers: "Lavora con noi",
      adminLogin: "Area Riservata",
    },
    common: {
      requestProposal: "Richiedi una proposta",
      discoverFormats: "Scopri i nostri format",
      discoverFormat: "Scopri il format",
      contactUs: "Contattaci ora",
      readMore: "Approfondisci",
      backHome: "Torna alla Home",
      sending: "Invio in corso...",
      send: "Invia richiesta",
      thankYou: "Grazie. Ti contatteremo a breve.",
      error: "Si è verificato un errore. Riprova.",
      learnMore: "Scopri di più",
    },
    hero: {
      eyebrow: "Dal 2015 · Milano · Food Empire",
      title: "Scudieri Group",
      subtitle:
        "Food truck, street food e ristorazione mobile per fiere, eventi e manifestazioni.",
      tag: "Dove arriva il pubblico, arriva Scudieri Group.",
      sticker: "Tutto è possibile",
      rotatorWords: ["Sogna.", "Crea.", "Realizza.", "Vivi."],
    },
    manifesto: {
      eyebrow: "Manifesto",
      lead:
        "Dal 2015 un'identità che racconta energia, passione e visione.",
      body:
        "Nata dal cuore e dalla testa di chi non si ferma mai. Imprenditoria che crea valore, ispira persone e trasforma i sogni in imprese reali.",
      claim: "Una mente, mille idee, tradizioni e innovazione.",
    },
    pillars: {
      eyebrow: "I 5 pilastri",
      title: "Cinque valori che ci muovono",
      subtitle:
        "Ogni evento, ogni format, ogni operazione nasce da questi cinque punti fermi.",
      items: [
        { t: "Passione", d: "che guida.", color: "gold" },
        { t: "Visione", d: "che lancia.", color: "green" },
        { t: "Mente", d: "che crea.", color: "white" },
        { t: "Relazioni", d: "che costruiscono.", color: "gold" },
        { t: "Obiettivi", d: "che realizzano.", color: "red" },
      ],
    },
    impact: {
      eyebrow: "Numeri & impatto",
      title: "Una struttura pensata per i grandi flussi",
      subtitle:
        "Il food service che si muove con gli eventi: organizzazione, format e operatività in un'unica regia.",
    },
    about: {
      eyebrow: "Chi siamo",
      title: "Una food company milanese, costruita per gli eventi.",
      body: "Scudieri Group S.r.l. nasce a Milano come realtà specializzata nella ristorazione mobile. Dal 2015 opera nel mondo degli eventi, delle fiere e delle manifestazioni, portando format food flessibili, veloci e riconoscibili dove il pubblico si incontra. Una struttura operativa pensata per grandi flussi, grandi eventi e grandi opportunità.",
      pillars: [
        "Approccio da food company",
        "Format gastronomici modulari",
        "Logistica e operatività dedicate",
        "Presenza in contesti fieristici e manifestazioni",
      ],
    },
    services: {
      eyebrow: "Cosa facciamo",
      title: "Un food service completo, modulare, riconoscibile",
      items: [
        { t: "Food Truck & Street Food", d: "Mezzi attrezzati e format dedicati per ogni tipologia di pubblico." },
        { t: "Fiere e Manifestazioni", d: "Punti food coordinati, gestione flussi e immagine integrata con il tuo evento." },
        { t: "Catering Operativo per Grandi Flussi", d: "Soluzioni scalabili capaci di servire migliaia di persone con costanza." },
        { t: "Aree Food Temporanee", d: "Progettazione e gestione di aree ristoro mobili, dal layout al personale." },
        { t: "Format Gastronomici Brandizzati", d: "Identità visiva, storytelling e menu costruiti su misura per l'evento." },
        { t: "Eventi Aziendali e Pubblici", d: "Dal corporate al festival: divisione Event Restaurant per cene esclusive." },
        { t: "Supporto Logistico & Operativo", d: "Allestimenti, mezzi, personale qualificato e coordinamento on-site." },
      ],
    },
    formats: {
      eyebrow: "I nostri mondi food",
      title: "Sette format. Una sola regia.",
      subtitle: "Tutti i nostri format sono personalizzabili in base all'evento, alla location e al pubblico atteso.",
    },
    organizers: {
      eyebrow: "Per chi organizza",
      title: "Un partner food per chi organizza eventi",
      body: "Scudieri Group supporta organizzatori, enti, aziende e location nella creazione di soluzioni food mobili, efficienti e coordinate.",
      bullets: [
        "Rapidità di allestimento",
        "Format adattabili alle dimensioni dell'evento",
        "Gestione flussi e tempi di servizio",
        "Immagine coordinata e riconoscibile",
        "Personale operativo qualificato",
        "Soluzioni scalabili da 500 a 50.000 partecipanti",
        "Esperienza nel pubblico evento e nelle fiere",
      ],
    },
    why: {
      eyebrow: "Perché sceglierci",
      title: "Non un food truck. Una food company.",
      points: [
        { t: "Esperienza dal 2015", d: "Quasi un decennio di operazioni nel food service itinerante." },
        { t: "Milano come base operativa", d: "Cuore logistico nel nord Italia, raggio d'azione nazionale." },
        { t: "Approccio da food company", d: "Strutture, processi e standard tipici di una grande organizzazione." },
        { t: "Capacità di adattamento", d: "Format scalabili e modulari per ogni tipo di evento." },
        { t: "Format riconoscibili", d: "Sette mondi food costruiti per emergere e funzionare." },
        { t: "Logistica e operatività", d: "Mezzi propri, personale dedicato, supply chain coordinata." },
        { t: "Presenza fieristica e manifestazioni", d: "Operativi in contesti complessi con grandi flussi di pubblico." },
      ],
    },
    cta: {
      eyebrow: "Pronto a partire?",
      title: "Stai organizzando un evento?",
      body: "Porta un'area food completa, riconoscibile e operativa. Scudieri Group costruisce la soluzione giusta per il tuo pubblico.",
    },
    gallery: {
      eyebrow: "Sul campo",
      title: "Frammenti di operatività",
      subtitle: "Scatti dei nostri allestimenti, format e momenti di servizio.",
    },
    events: {
      eyebrow: "Case history",
      title: "Eventi e manifestazioni",
      subtitle: "Una selezione di contesti in cui abbiamo operato. Ogni progetto è personalizzato.",
    },
    contact: {
      eyebrow: "Contatti",
      title: "Costruiamo insieme la tua area food.",
      subtitle: "Compila il form: il nostro team ti risponderà con una proposta dedicata.",
      fields: {
        name: "Nome e cognome",
        company: "Azienda / Ente",
        email: "Email",
        phone: "Telefono",
        eventType: "Tipo evento",
        eventDate: "Data evento",
        participants: "Partecipanti stimati",
        location: "Location",
        message: "Messaggio",
      },
      eventTypes: ["Fiera", "Manifestazione outdoor", "Evento aziendale", "Concerto / Festival", "Evento privato", "Altro"],
      contacts: "Contatti aziendali",
    },
    careers: {
      eyebrow: "Lavora con noi",
      title: "Costruisci il tuo percorso in una food company in crescita.",
      body: "Cerchiamo persone che amano l'operatività, il pubblico e il food service itinerante. Inviaci la tua candidatura.",
      cta: "Invia candidatura",
    },
    footer: {
      tagline: "Food truck, street food e ristorazione mobile per fiere, eventi e manifestazioni.",
      sitemap: "Sito",
      legal: "Area legale",
      privacy: "Privacy Policy",
      cookie: "Cookie Policy",
      cookieSettings: "Gestisci cookie",
      copy: "Tutti i diritti riservati.",
    },
    admin: {
      title: "Area Riservata",
      login: "Accedi",
      email: "Email",
      password: "Password",
      logout: "Esci",
      dashboard: "Dashboard CMS",
      formats: "Format",
      events: "Eventi",
      gallery: "Galleria",
      messages: "Richieste",
      settings: "Impostazioni",
      save: "Salva",
      cancel: "Annulla",
      add: "Nuovo",
      delete: "Elimina",
      confirmDelete: "Confermi l'eliminazione?",
      saved: "Salvato",
    },
  },
  en: {
    nav: {
      home: "Home",
      formats: "Formats",
      services: "Services",
      events: "Events",
      gallery: "Gallery",
      contact: "Contact",
      careers: "Careers",
      adminLogin: "Admin",
    },
    common: {
      requestProposal: "Request a proposal",
      discoverFormats: "Discover our formats",
      discoverFormat: "Explore format",
      contactUs: "Contact us now",
      readMore: "Learn more",
      backHome: "Back to home",
      sending: "Sending...",
      send: "Send request",
      thankYou: "Thank you. We will reach out soon.",
      error: "Something went wrong. Please try again.",
      learnMore: "Learn more",
    },
    hero: {
      eyebrow: "Since 2015 · Milan · Food Empire",
      title: "Scudieri Group",
      subtitle:
        "Food trucks, street food and mobile catering for fairs, events and large-scale shows.",
      tag: "Where the audience goes, Scudieri Group is already there.",
      sticker: "Anything is possible",
      rotatorWords: ["Dream.", "Create.", "Achieve.", "Live it."],
    },
    manifesto: {
      eyebrow: "Manifesto",
      lead:
        "Since 2015 — an identity made of energy, passion and vision.",
      body:
        "Born from the heart and mind of people who never stand still. Entrepreneurship that creates value, inspires people and turns dreams into real businesses.",
      claim: "One mind, a thousand ideas, tradition and innovation.",
    },
    pillars: {
      eyebrow: "The 5 pillars",
      title: "Five values that move us",
      subtitle:
        "Every event, every format, every operation starts from these five anchors.",
      items: [
        { t: "Passion", d: "that guides.", color: "gold" },
        { t: "Vision", d: "that launches.", color: "green" },
        { t: "Mind", d: "that creates.", color: "white" },
        { t: "Relationships", d: "that build.", color: "gold" },
        { t: "Goals", d: "that deliver.", color: "red" },
      ],
    },
    impact: {
      eyebrow: "Numbers & impact",
      title: "A structure built for large flows",
      subtitle:
        "Food service that moves with events: organisation, formats and operations under one roof.",
    },
    about: {
      eyebrow: "About us",
      title: "A Milan-based food company, built for events.",
      body: "Scudieri Group S.r.l. was born in Milan as a mobile catering specialist. Since 2015 we have operated in the world of events, fairs and shows, bringing flexible, fast and recognisable food formats wherever audiences gather. An operational structure designed for large flows, large events and large opportunities.",
      pillars: [
        "True food-company approach",
        "Modular gastronomic formats",
        "Dedicated logistics and operations",
        "Active in fairs and large shows",
      ],
    },
    services: {
      eyebrow: "What we do",
      title: "A complete, modular, recognisable food service",
      items: [
        { t: "Food Trucks & Street Food", d: "Equipped vehicles and dedicated formats for every audience type." },
        { t: "Fairs & Large Shows", d: "Coordinated food points, flow management and full visual integration with your event." },
        { t: "High-volume Catering", d: "Scalable solutions able to consistently serve thousands of people." },
        { t: "Temporary Food Areas", d: "Design and management of mobile food courts, from layout to staff." },
        { t: "Branded Food Formats", d: "Visual identity, storytelling and menus built around the event." },
        { t: "Corporate & Public Events", d: "From corporate to festivals: an Event Restaurant division for exclusive dinners." },
        { t: "Logistics & Operations Support", d: "Set-up, vehicles, qualified staff and on-site coordination." },
      ],
    },
    formats: {
      eyebrow: "Our food worlds",
      title: "Seven formats. One direction.",
      subtitle: "All our formats are tailor-made around the event, the venue and the expected audience.",
    },
    organizers: {
      eyebrow: "For organisers",
      title: "A food partner for event organisers",
      body: "Scudieri Group supports organisers, institutions, brands and venues in creating mobile, efficient and coordinated food solutions.",
      bullets: [
        "Fast set-up times",
        "Formats adapting to event size",
        "Flow and service-time management",
        "Coordinated, recognisable visual identity",
        "Qualified operational staff",
        "Scalable solutions from 500 to 50,000 attendees",
        "Track record in fairs and public events",
      ],
    },
    why: {
      eyebrow: "Why us",
      title: "Not a food truck. A food company.",
      points: [
        { t: "Operating since 2015", d: "Almost a decade of operations in mobile food service." },
        { t: "Milan operational hub", d: "Logistics core in northern Italy, nationwide reach." },
        { t: "Food-company approach", d: "Structures, processes and standards typical of a large organisation." },
        { t: "Adaptive capacity", d: "Scalable, modular formats for every kind of event." },
        { t: "Recognisable formats", d: "Seven food worlds built to stand out and to work." },
        { t: "Logistics & operations", d: "Owned vehicles, dedicated staff, coordinated supply chain." },
        { t: "Fairs & shows", d: "Active in complex contexts with large flows of people." },
      ],
    },
    cta: {
      eyebrow: "Ready to start?",
      title: "Are you organising an event?",
      body: "Bring on a complete, recognisable and fully operational food area. Scudieri Group builds the right solution for your audience.",
    },
    gallery: {
      eyebrow: "On the field",
      title: "Operational fragments",
      subtitle: "Snapshots of our set-ups, formats and service moments.",
    },
    events: {
      eyebrow: "Case history",
      title: "Events & shows",
      subtitle: "A selection of contexts where we have operated. Every project is tailor-made.",
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's build your food area together.",
      subtitle: "Fill in the form: our team will reply with a dedicated proposal.",
      fields: {
        name: "Full name",
        company: "Company / Organisation",
        email: "Email",
        phone: "Phone",
        eventType: "Event type",
        eventDate: "Event date",
        participants: "Estimated participants",
        location: "Location",
        message: "Message",
      },
      eventTypes: ["Trade fair", "Outdoor festival", "Corporate event", "Concert / Festival", "Private event", "Other"],
      contacts: "Company contacts",
    },
    careers: {
      eyebrow: "Careers",
      title: "Build your career in a growing food company.",
      body: "We're looking for people who love operations, audiences and itinerant food service. Send us your application.",
      cta: "Send application",
    },
    footer: {
      tagline: "Food trucks, street food and mobile catering for fairs, events and shows.",
      sitemap: "Site",
      legal: "Legal",
      privacy: "Privacy Policy",
      cookie: "Cookie Policy",
      cookieSettings: "Manage cookies",
      copy: "All rights reserved.",
    },
    admin: {
      title: "Admin Area",
      login: "Sign in",
      email: "Email",
      password: "Password",
      logout: "Sign out",
      dashboard: "CMS Dashboard",
      formats: "Formats",
      events: "Events",
      gallery: "Gallery",
      messages: "Messages",
      settings: "Settings",
      save: "Save",
      cancel: "Cancel",
      add: "New",
      delete: "Delete",
      confirmDelete: "Confirm deletion?",
      saved: "Saved",
    },
  },
};

// ---------------- Deep merge helpers ----------------
function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function deepMerge(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) && Array.isArray(override)) return override; // arrays replace
  if (isPlainObject(base) && isPlainObject(override)) {
    const out = { ...base };
    for (const k of Object.keys(override)) {
      out[k] = deepMerge(base[k], override[k]);
    }
    return out;
  }
  return override;
}

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_KEY) || "it");
  const [overrides, setOverrides] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const refreshContent = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setOverrides(data?.content_overrides || {});
    } catch {
      setOverrides({});
    }
  }, []);

  useEffect(() => {
    refreshContent();
  }, [refreshContent]);

  const baseT = translations[lang];
  const langOverrides = overrides && overrides[lang] ? overrides[lang] : {};
  const t = overrides ? deepMerge(baseT, langOverrides) : baseT;

  const toggle = () => setLang((l) => (l === "it" ? "en" : "it"));

  return (
    <LangContext.Provider value={{ lang, t, setLang, toggle, overrides, refreshContent }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
